import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface Agent3DProps {
  type: 'box' | 'sphere' | 'torus' | 'cylinder';
  color: string;
  size?: number;
  speed?: number;
  position?: [number, number, number];
  rotation?: [number, number, number];
}

export function Agent3D({ 
  type, 
  color, 
  size = 1, 
  speed = 1,
  position = [0, 0, 0],
  rotation = [0, 0, 0]
}: Agent3DProps) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.x = state.clock.elapsedTime * speed * 0.5;
      meshRef.current.rotation.y = state.clock.elapsedTime * speed * 0.3;
      meshRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * speed) * 0.2;
    }
  });

  const renderShape = () => {
    const commonProps = {
      ref: meshRef,
      position: position as [number, number, number],
      rotation: rotation as [number, number, number],
    };

    switch (type) {
      case 'box':
        return (
          <mesh {...commonProps}>
            <boxGeometry args={[size, size, size]} />
            <meshStandardMaterial 
              color={color} 
              transparent 
              opacity={0.8}
              metalness={0.3}
              roughness={0.2}
            />
          </mesh>
        );
      case 'sphere':
        return (
          <mesh {...commonProps}>
            <sphereGeometry args={[size / 2, 16, 16]} />
            <meshStandardMaterial 
              color={color} 
              transparent 
              opacity={0.8}
              metalness={0.3}
              roughness={0.2}
            />
          </mesh>
        );
      case 'torus':
        return (
          <mesh {...commonProps}>
            <torusGeometry args={[size / 2, size / 4, 8, 16]} />
            <meshStandardMaterial 
              color={color} 
              transparent 
              opacity={0.8}
              metalness={0.3}
              roughness={0.2}
            />
          </mesh>
        );
      case 'cylinder':
        return (
          <mesh {...commonProps}>
            <cylinderGeometry args={[size / 2, size / 2, size, 16]} />
            <meshStandardMaterial 
              color={color} 
              transparent 
              opacity={0.8}
              metalness={0.3}
              roughness={0.2}
            />
          </mesh>
        );
      default:
        return (
          <mesh {...commonProps}>
            <boxGeometry args={[size, size, size]} />
            <meshStandardMaterial 
              color={color} 
              transparent 
              opacity={0.8}
              metalness={0.3}
              roughness={0.2}
            />
          </mesh>
        );
    }
  };

  return renderShape();
}

// Agent swarm component for multiple agents
interface AgentSwarmProps {
  agents: Array<{
    id: string;
    type: Agent3DProps['type'];
    color: string;
    position: [number, number, number];
  }>;
}

export function AgentSwarm({ agents }: AgentSwarmProps) {
  return (
    <group>
      {agents.map((agent, index) => (
        <Agent3D
          key={agent.id}
          type={agent.type}
          color={agent.color}
          size={0.5 + (index % 3) * 0.2}
          speed={0.5 + (index % 4) * 0.3}
          position={agent.position}
        />
      ))}
    </group>
  );
}
